import {BrowserModule} from '@angular/platform-browser';
import {LOCALE_ID, NgModule} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {RouterModule, Routes} from '@angular/router';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {NgIdleKeepaliveModule} from '@ng-idle/keepalive';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import localeEsMx from '@angular/common/locales/es-MX';
import {registerLocaleData} from '@angular/common';
import {NgxSpinnerModule} from 'ngx-spinner';
import {NgxPaginationModule} from 'ngx-pagination';
import {AgGridModule} from 'ag-grid-angular';
import {SweetAlert2Module} from '@toverux/ngx-sweetalert2';

// APP MODULES
import {LoginComponent} from './login/login.component';
import {ContrasenaComponent} from './contrasena/contrasena.component';
import {ContrasenaListComponent} from './contrasena/contrasena-list.component';
import {ChangeContrasenaComponent} from './contrasena/change-contrasena.component';
import {DashboardComponent} from './dashboard/dashboard.component';
import {AuthGuard} from './services/auth.guard';
import {MonitorComponent} from './monitor/monitor.component';
import {MonitorService} from './shared/monitor.service';
import {MonitorFilterComponent} from './monitor/monitor-filter.component';
import {AuthService} from './services/auth.service';
import {RequestOptions} from '@angular/http';
import {CustomRequestOptions} from './services/custom.request.options';
import {TokenInterceptorService} from './services/token-interceptor.service';
import {MenuComponent} from './menu/menu.component';
import {HeaderComponent} from './header/header.component';
import {FooterComponent} from './footer/footer.component';
import {AddRolComponent} from './roles/add-rol.component';
import {EditRolComponent} from './roles/edit.component';
import {RolesComponent} from './roles/roles.component';
import {RolesListComponent} from './roles/roles-list.component';
import {RoleService} from './shared/role.service';
import {RolePermissionsComponent} from './roles/role-permissions.component';
import {AddUsuarioComponent} from './usuarios/add-usuario.component';
import {UsuariosComponent} from './usuarios/usuarios.component';
import {UsuariosListComponent} from './usuarios/usuarios-list.component';
import {EditUsuarioComponent} from './usuarios/edit-usuario.component';
import {AddModuloComponent} from './modulos/add-modulo.component';
import {EditModuloComponent} from './modulos/edit.component';
import {ModulosComponent} from './modulos/modulos.component';
import {ModulosListComponent} from './modulos/modulos-list.component';
import {AddPromocionComponent} from './promociones/add-promocion.component';
import {EditPromocionComponent} from './promociones/edit-promocion.component';
import {PromocionesComponent} from './promociones/promociones.component';
import {PromocionesListComponent} from './promociones/promociones-list.component';
import {PosesComponent} from './pos/poses.component';
import {PosesListComponent} from './pos/poses-list.component';
import {AddPosComponent} from './pos/add-pos.component';
import {EditPosComponent} from './pos/edit-pos.component';
import {SucursalesComponent} from './sucursales/sucursales.component';
import {SucursalesListComponent} from './sucursales/sucursales-list.component';
import {AddSucursalComponent} from './sucursales/add-sucursal.component';
import {EditSucursalComponent} from './sucursales/edit-sucursal.component';
import {TarjetasComponent} from './tarjetas/tarjetas.component';
import {TarjetasListComponent} from './tarjetas/tarjetas-list.component';
import {AddTarjetaComponent} from './tarjetas/add-tarjeta.component';
import {EditTarjetaComponent} from './tarjetas/edit-tarjeta.component';
import {EmisoresComponent} from './emisores/emisores.component';
import {EmisoresListComponent} from './emisores/emisores-list.component';
import {AddEmisorComponent} from './emisores/add-emisor.component';
import {EditEmisorComponent} from './emisores/edit-emisor.component';
import {BinComponent} from './bines/bin.component';
import {BinListComponent} from './bines/bin-list.component';
import {AddBinComponent} from './bines/add-bin.component';
import {EditBinComponent} from './bines/edit-bin.component';
import {ReportesComponent} from './reportes/reportes.component';
import {ReportesViewComponent} from './reportes/reportes-view.component';
import {ReportService} from './shared/report.service';
import {ReportFilterComponent} from './reportes/report-filter.component';
import {ProgressbarComponent} from './progressbar/progressbar.component';
import {RuteosListComponent} from './ruteo/ruteos-list.component';
import {RuteosComponent} from './ruteo/ruteos.component';
import {AddRuteoComponent} from './ruteo/add-ruteo.component';
import {EditRuteoComponent} from './ruteo/edit-ruteo.component';
import {HttpClientSecured} from './services/http-client-secured';
import {NgSelectModule} from '@ng-select/ng-select';
import {HttpErrorValidator} from './services/http-error-validator';
import {MerchantListComponent} from './merchant/merchant-list.component';
import {AddMerchantComponent} from './merchant/add-merchant.component';
import {EditMerchantComponent} from './merchant/edit-merchant.component';
import {MerchantComponent} from './merchant/merchant.component';
import {MatButtonModule} from '@angular/material/button';
import {MatStepperModule} from '@angular/material/stepper';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatCardModule} from '@angular/material/card';
import {MatTabsModule} from "@angular/material/tabs";
import { SearchMerchantComponent } from './merchant/search-merchant.component';
import { ModalSearchMerchantComponent } from './merchant/modal-search-merchant.component';

registerLocaleData(localeEsMx);

const appRoutes: Routes = [
  {path: '', component: LoginComponent},
  {path: 'contrasena', component: ContrasenaListComponent},
  // {path: 'contrasenar', component: ChangeContrasenaComponent},
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard]
  },
  {path: 'monitor', component: MonitorComponent, canActivate: [AuthGuard]},
  {path: 'monitord', component: MonitorComponent},
  {path: 'roles', component: RolesListComponent, canActivate: [AuthGuard]},
  {path: 'roles/edit', component: EditRolComponent, canActivate: [AuthGuard]},
  {path: 'reportes', component: ReportesComponent, canActivate: [AuthGuard]},
  {
    path: 'usuarios',
    component: UsuariosListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'usuarios/edit',
    component: EditUsuarioComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'modulos',
    component: ModulosListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'promociones',
    component: PromocionesListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'pos',
    component: PosesListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'sucursales',
    component: SucursalesListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'tarjetas',
    component: TarjetasListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'emisores',
    component: EmisoresListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'bines',
    component: BinListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'ruteo',
    component: RuteosListComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'comercios',
    component: MerchantListComponent,
    canActivate: [AuthGuard]
  }
];

@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    HeaderComponent,
    FooterComponent,
    LoginComponent,
    ContrasenaComponent,
    ContrasenaListComponent,
    ChangeContrasenaComponent,
    DashboardComponent,
    MonitorComponent,
    RolesComponent,
    AddRolComponent,
    EditRolComponent,
    RolesListComponent,
    RolePermissionsComponent,
    AddUsuarioComponent,
    UsuariosComponent,
    UsuariosListComponent,
    AddModuloComponent,
    EditModuloComponent,
    ModulosComponent,
    ModulosListComponent,
    AddPromocionComponent,
    EditPromocionComponent,
    PromocionesComponent,
    PromocionesListComponent,
    PosesComponent,
    PosesListComponent,
    AddPosComponent,
    EditPosComponent,
    SucursalesComponent,
    SucursalesListComponent,
    AddSucursalComponent,
    EditSucursalComponent,
    TarjetasComponent,
    TarjetasListComponent,
    AddTarjetaComponent,
    EditTarjetaComponent,
    EmisoresComponent,
    EmisoresListComponent,
    AddEmisorComponent,
    EditEmisorComponent,
    BinComponent,
    BinListComponent,
    AddBinComponent,
    EditBinComponent,
    // ServiciosComponent,
    // ServiciosListComponent,
    // SistemaEmisoresComponent,
    // SistemaEmisoresListComponent,
    // SistemaServiciosComponent,
    // SistemaServiciosListComponent,
    // SistemaSucursalesComponent,
    // ServicioSucursalesListComponent,
    // AddServicioComponent,
    EditUsuarioComponent,
    ReportesComponent,
    ReportesViewComponent,
    ProgressbarComponent,
    RuteosListComponent,
    RuteosComponent,
    AddRuteoComponent,
    EditRuteoComponent,
    MonitorFilterComponent,
    ReportFilterComponent,
    MerchantListComponent,
    AddMerchantComponent,
    EditMerchantComponent,
    MerchantComponent,
    SearchMerchantComponent,

    ModalSearchMerchantComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    NgbModule,
    NgIdleKeepaliveModule.forRoot(),
    BsDatepickerModule.forRoot(),
    RouterModule.forRoot(appRoutes, {useHash: true}),
    NgxSpinnerModule,
    NgxPaginationModule,
    AgGridModule.withComponents([]),
    NgSelectModule,
    SweetAlert2Module.forRoot({
      buttonsStyling: false,
      customClass: 'modal-content',
      confirmButtonClass: 'accept_mod',
      cancelButtonClass: 'cancel_mod'
    }),
    MatButtonModule,
    MatStepperModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatTabsModule
  ],
  providers: [
    AuthService,
    {
      provide: RequestOptions,
      useClass: CustomRequestOptions,
      multi: true
    },
    AuthGuard,
    MonitorService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true
    },
    RoleService,
    ReportService,
    {provide: LOCALE_ID, useValue: 'es-MX'},
    HttpClientSecured,
    HttpErrorValidator
  ],
  bootstrap: [AppComponent],
  entryComponents: [ProgressbarComponent]
})
export class AppModule {

}
